import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  IconButton,
  InputAdornment,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  type SelectChangeEvent
} from '@mui/material';
import { PersonPin, ArrowBack, Add, CheckCircle, Visibility, VisibilityOff, Search, FilterAltOff, Edit, Delete, Info, Email, Phone, Home, Badge, Cake, Wc, Public, School, Payments, CalendarToday, Notes, Category, AttachFile, Receipt } from '@mui/icons-material';
import { apiGet, apiPost, apiPatch, apiDelete } from '../../services/api';
import { superAdminService } from '../../services/superAdminService';
import { UserRole, type User } from '../../types/user';

interface RegisteredByRef {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface CourseRegistrationRecord {
  _id: string;
  registrationId?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  courseTitle: string;
  courseCategory: string;
  subCategory: string;
  studyLevel?: string;
  studySubLevel?: string;
  studyMode?: string;
  experience?: string;
  goals?: string;
  hearAboutUs?: string;
  status?: string;
  paymentStatus?: 'paid' | 'unpaid';
  attendanceStatus?: 'attended' | 'not_attended';
  source?: string;
  adminNotes?: string;
  createdAt: string;
  registeredBy?: RegisteredByRef | null;
  agreedAmount?: number;
  isNegotiable?: boolean;
  serviceAmount?: number;
  commissionPercentage?: number;
  commissionAmount?: number;
}

interface ServiceCategoryRecord {
  _id: string;
  title: string;
  amount: number;
  currency: string;
  commissionPercentage: number;
}

interface PayoutRecord {
  _id: string;
  focalPerson: string;
  amount: number;
  note?: string;
  proofOfPaymentUrl?: string;
  recordedBy?: { firstName: string; lastName: string } | null;
  createdAt: string;
}

const FocalPersonsPage: React.FC = () => {
  const [focalPersons, setFocalPersons] = useState<User[]>([]);
  const [registrations, setRegistrations] = useState<CourseRegistrationRecord[]>([]);
  const [serviceCategories, setServiceCategories] = useState<ServiceCategoryRecord[]>([]);
  const [payouts, setPayouts] = useState<PayoutRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<User | null>(null);

  const [paymentDialogTarget, setPaymentDialogTarget] = useState<CourseRegistrationRecord | null>(null);
  const [paymentAmountInput, setPaymentAmountInput] = useState('');
  const [confirmingPayment, setConfirmingPayment] = useState(false);

  const [detailsTarget, setDetailsTarget] = useState<CourseRegistrationRecord | null>(null);

  const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
  const [payoutAmountInput, setPayoutAmountInput] = useState('');
  const [payoutNoteInput, setPayoutNoteInput] = useState('');
  const [payoutProofFile, setPayoutProofFile] = useState<File | null>(null);
  const [recordingPayout, setRecordingPayout] = useState(false);
  const [deletePayoutTarget, setDeletePayoutTarget] = useState<PayoutRecord | null>(null);
  const [deletingPayout, setDeletingPayout] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', phone: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);

  const [editTarget, setEditTarget] = useState<User | null>(null);
  const [editing, setEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({ firstName: '', lastName: '', phone: '', password: '' });
  const [showEditPassword, setShowEditPassword] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [viewTarget, setViewTarget] = useState<User | null>(null);

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Filters for the focal persons list
  const [fpSearch, setFpSearch] = useState('');

  // Filters for the selected focal person's registrations
  const [regSearch, setRegSearch] = useState('');
  const [regCategoryFilter, setRegCategoryFilter] = useState('all');
  const [regPaymentFilter, setRegPaymentFilter] = useState('all');
  const [regAttendanceFilter, setRegAttendanceFilter] = useState('all');

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersRes, regsRes, categoriesRes, payoutsRes] = await Promise.all([
        superAdminService.getAllUsers({ role: 'focal_person', limit: 200 }),
        apiGet<any>('/course-registration'),
        apiGet<any>('/service-categories'),
        apiGet<any>('/focal-person-payouts')
      ]);

      let usersData: User[] = [];
      if (usersRes) {
        if (Array.isArray((usersRes as any).users)) {
          usersData = (usersRes as any).users;
        } else if ((usersRes as any).data?.users && Array.isArray((usersRes as any).data.users)) {
          usersData = (usersRes as any).data.users;
        } else if (Array.isArray((usersRes as any).data)) {
          usersData = (usersRes as any).data;
        }
      }
      setFocalPersons(usersData);
      setRegistrations(regsRes?.data || regsRes || []);
      setServiceCategories(categoriesRes?.data || []);
      setPayouts(payoutsRes?.data || []);
    } catch (error) {
      console.error('Error loading focal persons:', error);
      setErrorMessage('Failed to load focal persons');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const resetRegistrationFilters = () => {
    setRegSearch('');
    setRegCategoryFilter('all');
    setRegPaymentFilter('all');
    setRegAttendanceFilter('all');
  };

  useEffect(() => {
    resetRegistrationFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?._id]);

  const registrationsFor = (fp: User) => registrations.filter(r => r.registeredBy?._id === fp._id);

  const payoutsFor = (fp: User) => payouts.filter(p => p.focalPerson === fp._id);

  const rawSelectedRegistrations = selected ? registrationsFor(selected) : [];

  const registrationCategories = Array.from(
    new Set(rawSelectedRegistrations.map(r => r.courseCategory).filter(Boolean))
  );

  const selectedRegistrations = rawSelectedRegistrations.filter(reg => {
    const term = regSearch.trim().toLowerCase();
    const matchesSearch = !term ||
      `${reg.firstName} ${reg.lastName}`.toLowerCase().includes(term) ||
      reg.email?.toLowerCase().includes(term) ||
      reg.phone?.toLowerCase().includes(term);
    const matchesCategory = regCategoryFilter === 'all' || reg.courseCategory === regCategoryFilter;
    const matchesPayment = regPaymentFilter === 'all' || reg.paymentStatus === regPaymentFilter;
    const matchesAttendance = regAttendanceFilter === 'all' || reg.attendanceStatus === regAttendanceFilter;
    return matchesSearch && matchesCategory && matchesPayment && matchesAttendance;
  });

  const filteredFocalPersons = focalPersons.filter(fp => {
    const term = fpSearch.trim().toLowerCase();
    if (!term) return true;
    return `${fp.firstName} ${fp.lastName}`.toLowerCase().includes(term) || fp.email?.toLowerCase().includes(term);
  });

  const updatePaymentStatus = async (id: string, status: 'paid' | 'unpaid') => {
    try {
      await apiPatch(`/course-registration/${id}/payment-status`, { paymentStatus: status });
      setSuccessMessage(`Payment status updated to ${status}`);
      loadData();
    } catch (error) {
      setErrorMessage('Failed to update payment status');
    }
  };

  const categoryFor = (reg: CourseRegistrationRecord) => serviceCategories.find(sc => sc.title === reg.courseTitle) || null;

  // What the customer is expected to pay for this registration: the focal person's quoted
  // price if one was given, otherwise the category's default price.
  const amountFor = (reg: CourseRegistrationRecord): number | null => {
    if (reg.agreedAmount !== undefined && reg.agreedAmount !== null) return reg.agreedAmount;
    const category = categoryFor(reg);
    return category ? category.amount : null;
  };

  // Commission the focal person will earn: the actual confirmed amount once paid, otherwise
  // an estimate based on the quoted/default price and the category's commission rate.
  const commissionFor = (reg: CourseRegistrationRecord): { value: number; isEstimate: boolean } | null => {
    if (reg.paymentStatus === 'paid' && reg.commissionAmount !== undefined) {
      return { value: reg.commissionAmount, isEstimate: false };
    }
    const category = categoryFor(reg);
    const amount = amountFor(reg);
    if (!category || amount === null) return null;
    return { value: Math.round(amount * category.commissionPercentage / 100 * 100) / 100, isEstimate: true };
  };

  const countsFor = (fp: User) => {
    const own = registrationsFor(fp);
    const totalEarned = own
      .filter(r => r.paymentStatus === 'paid')
      .reduce((sum, r) => sum + (r.commissionAmount || 0), 0);
    const pendingEarned = own
      .filter(r => r.paymentStatus !== 'paid')
      .reduce((sum, r) => sum + (commissionFor(r)?.value || 0), 0);
    const totalPaidOut = payoutsFor(fp).reduce((sum, p) => sum + p.amount, 0);
    return {
      submitted: own.length,
      paid: own.filter(r => r.paymentStatus === 'paid').length,
      attended: own.filter(r => r.attendanceStatus === 'attended').length,
      totalEarned,
      pendingEarned,
      totalPaidOut,
      remaining: Math.max(0, totalEarned - totalPaidOut)
    };
  };

  const openPaymentDialog = (reg: CourseRegistrationRecord) => {
    setPaymentDialogTarget(reg);
    const category = categoryFor(reg);
    const prefill = reg.agreedAmount ?? reg.serviceAmount ?? category?.amount;
    setPaymentAmountInput(prefill !== undefined && prefill !== null ? String(prefill) : '');
  };

  const handleConfirmPayment = async () => {
    if (!paymentDialogTarget) return;
    const trimmed = paymentAmountInput.trim();
    if (trimmed !== '') {
      const parsed = Number(trimmed);
      if (isNaN(parsed) || parsed < 0) {
        setErrorMessage('Please enter a valid amount paid');
        return;
      }
    }
    setConfirmingPayment(true);
    try {
      await apiPatch(`/course-registration/${paymentDialogTarget._id}/payment-status`, {
        paymentStatus: 'paid',
        amountPaid: trimmed !== '' ? trimmed : undefined,
      });
      setSuccessMessage('Payment confirmed');
      setPaymentDialogTarget(null);
      loadData();
    } catch (error) {
      setErrorMessage('Failed to confirm payment');
    } finally {
      setConfirmingPayment(false);
    }
  };

  const openPayoutDialog = () => {
    setPayoutAmountInput('');
    setPayoutNoteInput('');
    setPayoutProofFile(null);
    setPayoutDialogOpen(true);
  };

  const handleRecordPayout = async () => {
    if (!selected) return;
    const trimmed = payoutAmountInput.trim();
    const parsed = Number(trimmed);
    if (!trimmed || isNaN(parsed) || parsed <= 0) {
      setErrorMessage('Please enter a valid cashout amount greater than 0');
      return;
    }
    setRecordingPayout(true);
    try {
      const formData = new FormData();
      formData.append('focalPerson', selected._id);
      formData.append('amount', String(parsed));
      if (payoutNoteInput.trim()) {
        formData.append('note', payoutNoteInput.trim());
      }
      if (payoutProofFile) {
        formData.append('proofOfPayment', payoutProofFile);
      }
      await apiPost('/focal-person-payouts', formData);
      setSuccessMessage('Cashout recorded');
      setPayoutDialogOpen(false);
      loadData();
    } catch (error) {
      setErrorMessage('Failed to record cashout');
    } finally {
      setRecordingPayout(false);
    }
  };

  const handleDeletePayout = async () => {
    if (!deletePayoutTarget) return;
    setDeletingPayout(true);
    try {
      await apiDelete(`/focal-person-payouts/${deletePayoutTarget._id}`);
      setSuccessMessage('Cashout record deleted');
      setDeletePayoutTarget(null);
      loadData();
    } catch (error) {
      setErrorMessage('Failed to delete cashout record');
    } finally {
      setDeletingPayout(false);
    }
  };

  const updateAttendanceStatus = async (id: string, status: 'attended' | 'not_attended') => {
    try {
      await apiPatch(`/course-registration/${id}/attendance-status`, { attendanceStatus: status });
      setSuccessMessage(`Attendance status updated to ${status}`);
      loadData();
    } catch (error) {
      setErrorMessage('Failed to update attendance status');
    }
  };

  const handleCreate = async () => {
    if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim() || formData.password.length < 8) {
      setErrorMessage('Please fill in first name, last name, email, and a password of at least 8 characters');
      return;
    }
    setCreating(true);
    try {
      await apiPost('/admin/users', {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim() || undefined,
        password: formData.password,
        role: UserRole.FOCAL_PERSON,
        isActive: true
      });
      setSuccessMessage('Focal person account created successfully');
      setCreateOpen(false);
      setFormData({ firstName: '', lastName: '', email: '', phone: '', password: '' });
      setShowPassword(false);
      loadData();
    } catch (error: any) {
      console.error('Error creating focal person:', error);
      setErrorMessage(error?.response?.data?.error || 'Failed to create focal person account');
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (fp: User) => {
    setEditTarget(fp);
    setEditFormData({
      firstName: fp.firstName || '',
      lastName: fp.lastName || '',
      phone: (fp as any).phone || '',
      password: ''
    });
    setShowEditPassword(false);
  };

  const handleEdit = async () => {
    if (!editTarget) return;
    if (!editFormData.firstName.trim() || !editFormData.lastName.trim()) {
      setErrorMessage('First name and last name are required');
      return;
    }
    if (editFormData.password && editFormData.password.length < 8) {
      setErrorMessage('New password must be at least 8 characters');
      return;
    }
    setEditing(true);
    try {
      const updateData: Record<string, any> = {
        firstName: editFormData.firstName.trim(),
        lastName: editFormData.lastName.trim(),
        phone: editFormData.phone.trim() || undefined
      };
      if (editFormData.password) {
        updateData.password = editFormData.password;
      }
      await superAdminService.updateUser(editTarget._id, updateData);
      setSuccessMessage('Focal person account updated successfully');
      setEditTarget(null);
      loadData();
    } catch (error: any) {
      console.error('Error updating focal person:', error);
      setErrorMessage(error?.response?.data?.error || 'Failed to update focal person account');
    } finally {
      setEditing(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await superAdminService.deleteUser(deleteTarget._id);
      setSuccessMessage('Focal person account deleted');
      setDeleteTarget(null);
      if (selected?._id === deleteTarget._id) setSelected(null);
      loadData();
    } catch (error) {
      console.error('Error deleting focal person:', error);
      setErrorMessage('Failed to delete focal person account');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {selected && (
            <IconButton onClick={() => setSelected(null)}>
              <ArrowBack />
            </IconButton>
          )}
          <PersonPin color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {selected ? `${selected.firstName} ${selected.lastName}'s Registrations` : 'Focal Persons'}
          </Typography>
        </Box>
        {selected ? (
          <Button variant="outlined" startIcon={<Info />} onClick={() => setViewTarget(selected)}>
            View Profile
          </Button>
        ) : (
          <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
            Create Focal Person
          </Button>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : selected ? (
        <>
          {(() => {
            const summary = countsFor(selected);
            const selectedPayouts = payoutsFor(selected);
            return (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Earnings & Payouts</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={4} md={2}>
                    <Typography variant="h6">{summary.submitted}</Typography>
                    <Typography variant="caption" color="text.secondary">Registered</Typography>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Typography variant="h6">{summary.paid}</Typography>
                    <Typography variant="caption" color="text.secondary">Paid Customers</Typography>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Typography variant="h6" color="success.main">RWF {summary.totalEarned.toFixed(2)}</Typography>
                    <Typography variant="caption" color="text.secondary">Total Earned</Typography>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Typography variant="h6" color="warning.main">RWF {summary.pendingEarned.toFixed(2)}</Typography>
                    <Typography variant="caption" color="text.secondary">Pending (Est.)</Typography>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Typography variant="h6">RWF {summary.totalPaidOut.toFixed(2)}</Typography>
                    <Typography variant="caption" color="text.secondary">Paid Out (Cashed)</Typography>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Typography variant="h6" color="primary.main">RWF {summary.remaining.toFixed(2)}</Typography>
                    <Typography variant="caption" color="text.secondary">Remaining Owed</Typography>
                  </Grid>
                </Grid>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {selectedPayouts.length} cashout record{selectedPayouts.length === 1 ? '' : 's'}
                  </Typography>
                  <Button variant="contained" startIcon={<Payments />} onClick={openPayoutDialog}>
                    Record Cashout
                  </Button>
                </Box>
                {selectedPayouts.length > 0 && (
                  <TableContainer sx={{ mt: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Amount</TableCell>
                          <TableCell>Note</TableCell>
                          <TableCell>Proof</TableCell>
                          <TableCell>Recorded By</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell align="right"></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedPayouts.map(p => (
                          <TableRow key={p._id}>
                            <TableCell>RWF {p.amount.toFixed(2)}</TableCell>
                            <TableCell>{p.note || '—'}</TableCell>
                            <TableCell>
                              {p.proofOfPaymentUrl ? (
                                <IconButton
                                  size="small"
                                  color="primary"
                                  component="a"
                                  href={p.proofOfPaymentUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  aria-label="View proof of payment"
                                >
                                  <Receipt fontSize="small" />
                                </IconButton>
                              ) : '—'}
                            </TableCell>
                            <TableCell>{p.recordedBy ? `${p.recordedBy.firstName} ${p.recordedBy.lastName}` : '—'}</TableCell>
                            <TableCell>{new Date(p.createdAt).toLocaleString()}</TableCell>
                            <TableCell align="right">
                              <IconButton size="small" color="error" onClick={() => setDeletePayoutTarget(p)} aria-label="Delete cashout record">
                                <Delete fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            );
          })()}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search customer"
                  placeholder="Name, email or phone"
                  value={regSearch}
                  onChange={(e) => setRegSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    label="Category"
                    value={regCategoryFilter}
                    onChange={(e: SelectChangeEvent) => setRegCategoryFilter(e.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    {registrationCategories.map(cat => (
                      <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Payment</InputLabel>
                  <Select
                    label="Payment"
                    value={regPaymentFilter}
                    onChange={(e: SelectChangeEvent) => setRegPaymentFilter(e.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="paid">Paid</MenuItem>
                    <MenuItem value="unpaid">Unpaid</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Attendance</InputLabel>
                  <Select
                    label="Attendance"
                    value={regAttendanceFilter}
                    onChange={(e: SelectChangeEvent) => setRegAttendanceFilter(e.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="attended">Attended</MenuItem>
                    <MenuItem value="not_attended">Not Attended</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <Button
                  fullWidth
                  startIcon={<FilterAltOff />}
                  onClick={resetRegistrationFilters}
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
          </Paper>
          <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 'bold', backgroundColor: 'primary.main', color: 'white' } }}>
                <TableCell>Customer</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Course</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Payment</TableCell>
                <TableCell>Commission</TableCell>
                <TableCell>Attendance</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell align="right">Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedRegistrations.map(reg => {
                const currency = categoryFor(reg)?.currency || 'RWF';
                const amount = amountFor(reg);
                const commission = commissionFor(reg);
                return (
                <TableRow key={reg._id} hover>
                  <TableCell>{reg.firstName} {reg.lastName}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{reg.email}</Typography>
                    <Typography variant="caption" color="text.secondary">{reg.phone}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{reg.courseTitle}</Typography>
                    <Typography variant="caption" color="text.secondary">{reg.subCategory}</Typography>
                  </TableCell>
                  <TableCell>
                    {reg.isNegotiable ? (
                      <Chip label="Negotiable" size="small" color="warning" variant="outlined" />
                    ) : amount !== null ? (
                      <>
                        <Typography variant="body2">{currency} {amount.toFixed(2)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {reg.agreedAmount !== undefined && reg.agreedAmount !== null ? 'Quoted by focal person' : 'Category default'}
                        </Typography>
                      </>
                    ) : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={reg.paymentStatus === 'paid' ? <CheckCircle fontSize="small" /> : undefined}
                      label={reg.paymentStatus === 'paid' ? 'Paid' : 'Unpaid'}
                      color={reg.paymentStatus === 'paid' ? 'success' : 'warning'}
                      size="small"
                      variant="outlined"
                      onClick={() => (reg.paymentStatus === 'paid' ? updatePaymentStatus(reg._id, 'unpaid') : openPaymentDialog(reg))}
                      sx={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell>
                    {commission ? (
                      <Chip
                        label={`${currency} ${commission.value.toFixed(2)}${commission.isEstimate ? ' (est.)' : ''}`}
                        size="small"
                        color="primary"
                        variant={commission.isEstimate ? 'filled' : 'outlined'}
                        onClick={() => openPaymentDialog(reg)}
                        sx={{ cursor: 'pointer', ...(commission.isEstimate ? { opacity: 0.7 } : {}) }}
                      />
                    ) : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={reg.attendanceStatus === 'attended' ? <CheckCircle fontSize="small" /> : undefined}
                      label={reg.attendanceStatus === 'attended' ? 'Attended' : 'Not Attended'}
                      color={reg.attendanceStatus === 'attended' ? 'success' : 'default'}
                      size="small"
                      variant="outlined"
                      onClick={() => updateAttendanceStatus(reg._id, reg.attendanceStatus === 'attended' ? 'not_attended' : 'attended')}
                      sx={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell>{new Date(reg.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => setDetailsTarget(reg)} aria-label="View full registration details">
                      <Info fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
                );
              })}
              {selectedRegistrations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                      {rawSelectedRegistrations.length === 0
                        ? 'No registrations submitted yet.'
                        : 'No registrations match your filters.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </TableContainer>
        </>
      ) : (
        <>
          <TextField
            fullWidth
            size="small"
            label="Search focal persons"
            placeholder="Name or email"
            value={fpSearch}
            onChange={(e) => setFpSearch(e.target.value)}
            sx={{ mb: 2, maxWidth: 400 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Grid container spacing={2}>
            {filteredFocalPersons.map(fp => {
              const counts = countsFor(fp);
              return (
                <Grid item xs={12} sm={6} md={4} key={fp._id}>
                  <Card sx={{ cursor: 'pointer', height: '100%' }} onClick={() => setSelected(fp)}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar src={fp.profilePicture || fp.avatar} sx={{ bgcolor: 'primary.main' }}>
                            {fp.firstName?.[0]}{fp.lastName?.[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {fp.firstName} {fp.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">{fp.email}</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex' }}>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); setViewTarget(fp); }}
                            aria-label="View focal person profile"
                          >
                            <Info fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); openEdit(fp); }}
                            aria-label="Edit focal person"
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => { e.stopPropagation(); setDeleteTarget(fp); }}
                            aria-label="Delete focal person"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(108px, 1fr))', gap: 1 }}>
                        <Chip label={`${counts.submitted} submitted`} size="small" sx={{ width: '100%' }} />
                        <Chip label={`${counts.paid} paid`} size="small" color="success" variant="outlined" sx={{ width: '100%' }} />
                        <Chip label={`${counts.attended} attended`} size="small" color="info" variant="outlined" sx={{ width: '100%' }} />
                        <Chip label={`RWF ${counts.totalEarned.toFixed(2)} earned`} size="small" color="primary" variant="outlined" sx={{ width: '100%' }} />
                        <Chip label={`RWF ${counts.pendingEarned.toFixed(2)} pending`} size="small" color="warning" variant="outlined" sx={{ width: '100%' }} />
                        <Chip label={`RWF ${counts.remaining.toFixed(2)} owed`} size="small" color="secondary" variant="outlined" sx={{ width: '100%' }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
            {filteredFocalPersons.length === 0 && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                  {focalPersons.length === 0
                    ? 'No focal person accounts yet. Click "Create Focal Person" to add one.'
                    : 'No focal persons match your search.'}
                </Typography>
              </Grid>
            )}
          </Grid>
        </>
      )}

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Focal Person</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                helperText="Minimum 8 characters"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(prev => !prev)}
                        edge="end"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            {creating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editTarget} onClose={() => setEditTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Focal Person</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={editFormData.firstName}
                onChange={(e) => setEditFormData(prev => ({ ...prev, firstName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={editFormData.lastName}
                onChange={(e) => setEditFormData(prev => ({ ...prev, lastName: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                value={editTarget?.email || ''}
                disabled
                helperText="Email cannot be changed"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone"
                value={editFormData.phone}
                onChange={(e) => setEditFormData(prev => ({ ...prev, phone: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="New Password"
                type={showEditPassword ? 'text' : 'password'}
                value={editFormData.password}
                onChange={(e) => setEditFormData(prev => ({ ...prev, password: e.target.value }))}
                helperText="Leave blank to keep the current password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowEditPassword(prev => !prev)}
                        edge="end"
                        aria-label={showEditPassword ? 'Hide password' : 'Show password'}
                      >
                        {showEditPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleEdit} disabled={editing}>
            {editing ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Focal Person</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to permanently delete{' '}
            <strong>{deleteTarget?.firstName} {deleteTarget?.lastName}</strong>'s account? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!viewTarget} onClose={() => setViewTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Focal Person Profile</DialogTitle>
        <DialogContent>
          {viewTarget && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar src={viewTarget.profilePicture || viewTarget.avatar} sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                  {viewTarget.firstName?.[0]}{viewTarget.lastName?.[0]}
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {viewTarget.firstName} {viewTarget.lastName}
                  </Typography>
                  <Chip
                    label={viewTarget.isActive ? 'Active' : 'Suspended'}
                    color={viewTarget.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(108px, 1fr))', gap: 1, mb: 2 }}>
                {(() => {
                  const counts = countsFor(viewTarget);
                  return (
                    <>
                      <Chip label={`${counts.submitted} submitted`} size="small" sx={{ width: '100%' }} />
                      <Chip label={`${counts.paid} paid`} size="small" color="success" variant="outlined" sx={{ width: '100%' }} />
                      <Chip label={`${counts.attended} attended`} size="small" color="info" variant="outlined" sx={{ width: '100%' }} />
                      <Chip label={`RWF ${counts.totalEarned.toFixed(2)} earned`} size="small" color="primary" variant="outlined" sx={{ width: '100%' }} />
                      <Chip label={`RWF ${counts.pendingEarned.toFixed(2)} pending`} size="small" color="warning" variant="outlined" sx={{ width: '100%' }} />
                      <Chip label={`RWF ${counts.remaining.toFixed(2)} owed`} size="small" color="secondary" variant="outlined" sx={{ width: '100%' }} />
                    </>
                  );
                })()}
              </Box>

              <Divider sx={{ mb: 1 }} />

              <List dense>
                <ListItem>
                  <ListItemIcon><Email fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Email" secondary={viewTarget.email || '—'} />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Phone fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Phone" secondary={viewTarget.phone || '—'} />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Home fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Address" secondary={viewTarget.address || '—'} />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Badge fontSize="small" /></ListItemIcon>
                  <ListItemText primary="National ID / Passport Number" secondary={viewTarget.idNumber || '—'} />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Cake fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Date of Birth" secondary={viewTarget.dateOfBirth || '—'} />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Wc fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Gender" secondary={viewTarget.gender || '—'} />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Public fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Nationality" secondary={viewTarget.nationality || '—'} />
                </ListItem>
              </List>

              <Divider sx={{ my: 1 }} />

              <Typography variant="caption" color="text.secondary" display="block">
                Joined {viewTarget.createdAt ? new Date(viewTarget.createdAt).toLocaleDateString() : '—'}
                {viewTarget.lastLogin && ` • Last login ${new Date(viewTarget.lastLogin).toLocaleString()}`}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => { const fp = viewTarget; setViewTarget(null); if (fp) openEdit(fp); }}
            startIcon={<Edit />}
          >
            Edit
          </Button>
          <Button variant="contained" onClick={() => setViewTarget(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!paymentDialogTarget} onClose={() => setPaymentDialogTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Payment</DialogTitle>
        <DialogContent>
          {paymentDialogTarget && (() => {
            const category = categoryFor(paymentDialogTarget);
            const currency = category?.currency || 'RWF';
            const commissionPercentage = category?.commissionPercentage;
            const parsedInput = parseFloat(paymentAmountInput);
            const previewCommission = commissionPercentage !== undefined && !isNaN(parsedInput) && parsedInput >= 0
              ? Math.round(parsedInput * commissionPercentage / 100 * 100) / 100
              : null;
            return (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {paymentDialogTarget.firstName} {paymentDialogTarget.lastName} — {paymentDialogTarget.courseTitle}
                </Typography>
                {paymentDialogTarget.isNegotiable ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    The focal person marked this price as negotiable — enter the amount the customer actually paid.
                  </Alert>
                ) : paymentDialogTarget.agreedAmount !== undefined && paymentDialogTarget.agreedAmount !== null ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    The focal person quoted {currency} {paymentDialogTarget.agreedAmount.toFixed(2)}. You can change this before confirming.
                  </Alert>
                ) : category ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    No price was quoted by the focal person. Suggested price: {currency} {category.amount.toFixed(2)}.
                  </Alert>
                ) : null}
                <TextField
                  fullWidth
                  autoFocus
                  label="Amount Paid"
                  type="number"
                  value={paymentAmountInput}
                  onChange={(e) => setPaymentAmountInput(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">{currency}</InputAdornment>,
                  }}
                />
                {commissionPercentage !== undefined && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {previewCommission !== null
                      ? `Focal person commission: ${currency} ${previewCommission.toFixed(2)} (${commissionPercentage}%)`
                      : `Commission rate for this service: ${commissionPercentage}%`}
                  </Typography>
                )}
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogTarget(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmPayment} disabled={confirmingPayment}>
            {confirmingPayment ? 'Confirming...' : 'Confirm Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={payoutDialogOpen} onClose={() => setPayoutDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Record Cashout</DialogTitle>
        <DialogContent>
          {selected && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter the amount {selected.firstName} {selected.lastName} requested (or was paid) as a cashout. This reduces their
              remaining owed balance. Currently owed: RWF {countsFor(selected).remaining.toFixed(2)}.
            </Typography>
          )}
          <TextField
            fullWidth
            autoFocus
            label="Cashout Amount"
            type="number"
            value={payoutAmountInput}
            onChange={(e) => setPayoutAmountInput(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">RWF</InputAdornment>,
            }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Note (optional)"
            placeholder="e.g. Mobile money transfer reference"
            value={payoutNoteInput}
            onChange={(e) => setPayoutNoteInput(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Button
            component="label"
            variant="outlined"
            startIcon={<AttachFile />}
            fullWidth
          >
            {payoutProofFile ? payoutProofFile.name : 'Upload Proof of Payment (optional)'}
            <input
              type="file"
              hidden
              accept="image/*,application/pdf"
              onChange={(e) => setPayoutProofFile(e.target.files?.[0] || null)}
            />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayoutDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleRecordPayout} disabled={recordingPayout}>
            {recordingPayout ? 'Recording...' : 'Record Cashout'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deletePayoutTarget} onClose={() => setDeletePayoutTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Cashout Record</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this cashout record of{' '}
            <strong>RWF {deletePayoutTarget?.amount.toFixed(2)}</strong>? This will increase the focal person's remaining owed
            balance again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePayoutTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeletePayout} disabled={deletingPayout}>
            {deletingPayout ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!detailsTarget} onClose={() => setDetailsTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Registration Details</DialogTitle>
        <DialogContent>
          {detailsTarget && (() => {
            const currency = categoryFor(detailsTarget)?.currency || 'RWF';
            const amount = amountFor(detailsTarget);
            const commission = commissionFor(detailsTarget);
            return (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                    {detailsTarget.firstName?.[0]}{detailsTarget.lastName?.[0]}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {detailsTarget.firstName} {detailsTarget.lastName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {detailsTarget.registrationId || detailsTarget._id}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  <Chip label={detailsTarget.status || 'pending'} size="small" />
                  <Chip
                    label={detailsTarget.paymentStatus === 'paid' ? 'Paid' : 'Unpaid'}
                    size="small"
                    color={detailsTarget.paymentStatus === 'paid' ? 'success' : 'warning'}
                    variant="outlined"
                  />
                  <Chip
                    label={detailsTarget.attendanceStatus === 'attended' ? 'Attended' : 'Not Attended'}
                    size="small"
                    color={detailsTarget.attendanceStatus === 'attended' ? 'success' : 'default'}
                    variant="outlined"
                  />
                  {detailsTarget.registeredBy && (
                    <Chip
                      label={`Registered by ${detailsTarget.registeredBy.firstName} ${detailsTarget.registeredBy.lastName}`}
                      size="small"
                      color="info"
                      variant="outlined"
                    />
                  )}
                </Box>

                <Divider sx={{ mb: 1 }} />

                <List dense>
                  <ListItem>
                    <ListItemIcon><Email fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Email" secondary={detailsTarget.email || '—'} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><Phone fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Phone" secondary={detailsTarget.phone || '—'} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><Category fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Service"
                      secondary={`${detailsTarget.courseTitle} (${detailsTarget.courseCategory}) — ${detailsTarget.subCategory}`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><School fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Study Details"
                      secondary={
                        [
                          detailsTarget.studyLevel && `Level: ${detailsTarget.studyLevel}`,
                          detailsTarget.studySubLevel && `Papers: ${detailsTarget.studySubLevel}`,
                          detailsTarget.studyMode && `Mode: ${detailsTarget.studyMode}`,
                        ].filter(Boolean).join(' · ') || '—'
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><Notes fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Experience" secondary={detailsTarget.experience || '—'} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><Notes fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Goals" secondary={detailsTarget.goals || '—'} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><Info fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Heard About Us Via" secondary={detailsTarget.hearAboutUs || '—'} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><Payments fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Amount Customer Pays"
                      secondary={
                        detailsTarget.isNegotiable
                          ? 'Negotiable — not yet agreed'
                          : amount !== null
                            ? `${currency} ${amount.toFixed(2)} ${detailsTarget.agreedAmount !== undefined && detailsTarget.agreedAmount !== null ? '(quoted by focal person)' : '(category default — not yet quoted)'}`
                            : 'Not configured'
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><Payments fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Focal Person Commission"
                      secondary={
                        commission
                          ? `${currency} ${commission.value.toFixed(2)} ${commission.isEstimate ? '(estimated — payment not yet confirmed)' : '(confirmed)'} at ${categoryFor(detailsTarget)?.commissionPercentage ?? detailsTarget.commissionPercentage ?? 0}%`
                          : 'Not available — no matching service category'
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CalendarToday fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Submitted"
                      secondary={`${new Date(detailsTarget.createdAt).toLocaleString()}${detailsTarget.source ? ` · Source: ${detailsTarget.source}` : ''}`}
                    />
                  </ListItem>
                  {detailsTarget.adminNotes && (
                    <ListItem>
                      <ListItemIcon><Notes fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Admin Notes" secondary={detailsTarget.adminNotes} />
                    </ListItem>
                  )}
                </List>
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setDetailsTarget(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!successMessage} autoHideDuration={4000} onClose={() => setSuccessMessage(null)}>
        <Alert severity="success" onClose={() => setSuccessMessage(null)}>{successMessage}</Alert>
      </Snackbar>
      <Snackbar open={!!errorMessage} autoHideDuration={5000} onClose={() => setErrorMessage(null)}>
        <Alert severity="error" onClose={() => setErrorMessage(null)}>{errorMessage}</Alert>
      </Snackbar>
    </Container>
  );
};

export default FocalPersonsPage;
